import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Plus, FileSpreadsheet } from 'lucide-react';
import { CensusTemplateDownload } from './CensusTemplateDownload';
import { EmployeeCensusBuilder } from './EmployeeCensusBuilder';
import { DependentCensusBuilder } from './DependentCensusBuilder';
import { CobraCensusBuilder } from './CobraCensusBuilder';
import type { Application } from '@/types/database.types';

interface CensusManagerProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const CensusManager = ({ data, onSave }: CensusManagerProps) => {
  const [activeTab, setActiveTab] = useState('builder');
  const [showBuilder, setShowBuilder] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <FileSpreadsheet className="h-5 w-5" />
          Census Information
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          Choose to download templates for offline completion or build your census data directly in the app.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="builder">Online Builder</TabsTrigger>
            <TabsTrigger value="templates">Download Templates</TabsTrigger>
          </TabsList>
          
          <TabsContent value="builder" className="space-y-4">
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
              <p className="text-sm text-muted-foreground">
                Build your census data directly in the app. Data is automatically saved and can be exported later.
              </p>
            </div>
            
            <Tabs defaultValue="employees" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="employees">Employee Census</TabsTrigger>
                <TabsTrigger value="dependents">Dependent Census</TabsTrigger>
                <TabsTrigger value="cobra">COBRA Census</TabsTrigger>
              </TabsList>
              
              <TabsContent value="employees">
                <EmployeeCensusBuilder applicationId={data.id} onSave={onSave} />
              </TabsContent>
              
              <TabsContent value="dependents">
                <DependentCensusBuilder applicationId={data.id} onSave={onSave} />
              </TabsContent>
              
              <TabsContent value="cobra">
                <CobraCensusBuilder applicationId={data.id} onSave={onSave} />
              </TabsContent>
            </Tabs>
          </TabsContent>
          
          <TabsContent value="templates" className="space-y-4">
            <CensusTemplateDownload />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};